// Styles
import 'react-datepicker/dist/react-datepicker.css';
import 'scss/advanced_filters.scss';
import 'scss/layout.scss';

// External Modules
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import * as R from 'ramda';
import ReactTooltip from 'react-tooltip';
import {debounce} from 'lodash';

// SN Modules

/**
 * ********************************************
 * NOTE: Some query transformations occur in CampaignFilterSelect so when adding
 * new filters, make sure to add any necessary transformations made in this
 * component to that one as well
 * ********************************************
 */
import {
    AutomatedFilterSelect,
    CampaignFilterSelect
} from 'js/publisher_leads/campaign_filter_select';
import ExpandablePanel from 'js/components/expandable_panel';
import HelpBubble from 'js/components/help_bubble';
import Input from 'js/form_components/form_input';
import FormLabel from 'js/form_components/form_label';
import LoadingSpinner from 'js/components/loading_spinner';
import Button from './button';
import MultiSelectInputGroup from './multi_select_group/index';
import InvertibleInput from './invertible_input';
import InputDatePicker from './input_date_picker';
import InputNumber from './input_number';
import InputRange from './input_range';
import InputSelect from './input_select';
import InputTags from './input_tags';
import Checkbox from './check_box';
import FlightFinder from './flight_finder';
import FilterProcessor from './filter_processor';

const {CampaignType, ScoringStrategy} = window.sn.enums;

const SearchType = {LEADS: 'leads', POSTS: 'posts'};

const {
    content_analysis_tags,
    interests,
    sourcers,
    countries,
    contentTypeSpecialties,
    languages,
    regions,
    publisherTypes,
    campaignTypes,
    scoringStrategies,
    metricsSearchableCampaignTypes,
    allBrands
} = window.sn.filters;

const BLACKLIST_BRAND_TAG_PREFIX = 'blacklist-brandID-';
const BRANDS_BY_ID = R.reduce((acc, {id, name}) => R.merge(acc, {[id]: name}), {}, allBrands || []);

const registrationStatuses = [
    {value: 'yes', label: 'registered'},
    {value: 'no', label: 'unregistered'}
];

/*
    NOTE:
        When adding new variables here, make sure they map
        *exactly* to their column in the crm_filters model,
        so this doesn't mismatch when it's being populated
        from existing crm_filters_campaigns.
*/
export const initialState = {
    search_type: SearchType.LEADS,
    lucene_query: null,
    // saved for react-datepicker
    last_imported_start_moment: null,
    last_imported_end_moment: null,
    // filter form
    sourced_by: null,
    last_imported_at_start: null,
    last_imported_at_end: null,
    include_tags: null,
    exclude_tags: null,
    include_key_value_tags_v2: [{key: null, value: null}],
    exclude_key_value_tags_v2: [{key: null, value: null}],
    include_content_analysis_tags: [{key: null, value: null}],
    exclude_content_analysis_tags: [{key: null, value: null}],
    include_interests: null,
    exclude_interests: null,
    min_engagement_rate: null,
    max_engagement_rate: null,
    min_engagements_per_post: null,
    max_engagements_per_post: null,
    has_approved_invite_for_campaigns: null,
    has_no_approved_invite_for_campaigns: null,
    invert_has_approved_invite_for_campaigns: null,
    has_approved_invite_for_flight: null,
    min_pending_invites: null,
    max_pending_invites: null,
    min_non_pending_invites: null,
    max_non_pending_invites: null,
    min_posts_on_account: null,
    max_posts_on_account: null,
    min_invites_sent: null,
    max_invites_sent: null,
    min_admin_approved_campaigns: null,
    max_admin_approved_campaigns: null,
    metrics_platform: null,
    min_followers: null,
    max_followers: null,
    min_reference_payout: null,
    max_reference_payout: null,
    content_type_specialty: null,
    exclude_content_type_specialty: null,
    invert_content_type_specialty: null,
    language: null,
    invert_language: null,
    exclude_language: null,
    lead_country: null,
    invert_lead_country: null,
    exclude_lead_country: null,
    profile_country: null,
    exclude_profile_country: null,
    city: null,
    region: null,
    has_lead_for_platform: null,
    has_auth_for_platform: null,
    publisher_type: null,
    invert_publisher_type: null,
    exclude_publisher_type: null,
    age_range: null,
    invert_age_range: null,
    exclude_age_range: null,
    age_range_min: null,
    age_range_max: null,
    is_parent: null,
    children_age_range: null,
    children_age_range_min: null,
    children_age_range_max: null,
    avg_views_per_video_min: null,
    avg_views_per_video_max: null,
    recent_video_percent_min: null,
    recent_video_percent_max: null,
    recent_video_views_min: null,
    recent_video_views_max: null,
    true_video_engagement_rate_min: null,
    true_video_engagement_rate_max: null,
    video_view_rate_min: null,
    video_view_rate_max: null,
    video_engagement_rate_min: null,
    video_engagement_rate_max: null,
    min_accepted_posts: null,
    max_accepted_posts: null,
    min_rejected_posts: null,
    max_rejected_posts: null,
    min_avg_pq: null,
    max_avg_pq: null,
    min_avg_video_quality: null,
    max_avg_video_quality: null,
    has_approved_post_for_campaigns: null,
    registered: null,
    has_no_invite_for_campaigns: null,
    demographics_age_range: null,
    demographics_age_range_min: null,
    demographics_age_range_max: null,
    demographics_publisher_types: null,
    demographics_publisher_types_confidence: null,
    demographics_include_interests: null,
    demographics_include_interests_confidence: null,
    demographics_countries: null,
    demographics_cities: null,
    has_phone_number: null,
    has_complete_profile: null,
    has_incomplete_profile: null,
    scoring_strategy: null,
    boost_creators_for_brand_id: null,
    max_new_results: null,
    max_reactivated_results: null,
    min_score: null,
    min_comment_engagement_percentage: null,
    max_comment_engagement_percentage: null,
    min_avg_invite_score: null,
    max_avg_invite_score: null,
    latitude: null,
    longitude: null,
    distance_miles: null,
    tags: [],
    key_value_tags: [],
    allCampaigns: []
};

const getOptionFromEnumValue = (enumObjs, val) => ({
    label: R.find(R.propEq('value', val), enumObjs).text,
    value: val
});
// NOTE: When setting default state values, make sure to add a null initialState value as well.
const defaultState = R.merge(initialState, {
    searchType: SearchType.LEADS,
    metrics_platform: getOptionFromEnumValue(campaignTypes, CampaignType.INSTAGRAM),
    exclude_tags: R.map(value => ({value, label: value}), [
        'blacklist',
        'blacklist-IGquality',
        'blacklist-IGnotbrandsafe',
        'blacklist-IGprivate',
        'blacklist-IGfraud',
        'blacklist-unsubscribed',
        'blacklist-other',
        'blacklist-invite-fatigued',
        'blacklist-content-not-created-6mo',
        'blacklist-bounced-emails',
        'blacklist-unsubscribed-emails',
        'blacklist-late-content-6mo'
    ]),
    min_followers: 1000,
    min_posts_on_account: 50,
    scoring_strategy: getOptionFromEnumValue(scoringStrategies, ScoringStrategy.SECRET_SAUCE),
    has_complete_profile: true,
    has_incomplete_profile: true,
    has_phone_number: false
});

const experimentalFilters = [];

export default class AdvancedFilters extends Component {
    static propTypes = {
        showQueryGuide: PropTypes.bool,
        showAdvancedOptions: PropTypes.bool,
        onFiltersApplied: PropTypes.func.isRequired,
        applyFiltersCopy: PropTypes.string,
        searchTypeEnum: PropTypes.object.isRequired,
        tagStatsUrl: PropTypes.string
    };

    static areExperimentalFiltersUsed(filters) {
        return R.any(name => filters[name] !== defaultState[name], experimentalFilters);
    }

    constructor(props) {
        super(props);
        const {showAdvancedOptions = true} = props;
        this.state = R.mergeAll([initialState, defaultState, props.filters]);
        this.state.showAdvancedOptions = showAdvancedOptions;
        // inherit from FilterProcessor without having FilterProcessor be a component.
        this.filterProcessor = new FilterProcessor();
    }

    componentDidMount() {
        this.fetchCampaignFilterData();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const {allCampaigns, filters} = this.props;

        if (!R.equals(filters, nextProps.filters)) {
            this.setState(this.filterProcessor.prepareFilter(nextProps.filters, allCampaigns));
        }
    }

    fetchCampaignFilterData = async () => {
        const {getCampaignFilterDataUrl, allCampaigns} = this.props;

        if (!getCampaignFilterDataUrl) return;

        const response = await axios.get(getCampaignFilterDataUrl);

        if (R.path(['data', 'filters'], response)) {
            this.setState(this.filterProcessor.mergeProps(response.data.filters, allCampaigns));
        }
    };

    resetState = () => {
        this.setState(initialState);
    };

    resetStateToDefault = () => {
        this.setState(R.merge(initialState, defaultState));
    };

    handleSearchTypeChange = state => {
        const {searchTypeEnum} = this.props;
        const searchType = state ? searchTypeEnum.LEADS : searchTypeEnum.POSTS;
        this.setState({searchType});
    };

    handleLastImportedStartChange = date => {
        this.setState({
            last_imported_at_start: date ? date.format('YYYY-MM-DD') : null,
            last_imported_start_moment: date
        });
    };

    handleLastImportedEndChange = date => {
        this.setState({
            last_imported_at_end: date ? date.format('YYYY-MM-DD') : null,
            last_imported_end_moment: date
        });
    };

    handleInputChange = (field, data) =>
        this.setState({
            [field]: data || null
        });

    handleCheckboxChange = field =>
        this.setState({
            [field]: !this.state[field]
        });

    /**
     * @param {Object} filters
     * @memberof AdvancedFilters
     */
    handleFilterSelect = filters => {
        const {allCampaigns} = this.props;

        const newState = R.merge(
            initialState,
            this.filterProcessor.parseFilter(filters, allCampaigns)
        );
        this.setState(newState);
    };

    handleApplyFilterClick = () => {
        const encoded = this.filterProcessor.encodeFilter(this.state);
        this.props.onFiltersApplied(encoded, this.state);
    };

    handleClearFilterClick = () => {
        this.resetState();
    };

    /**
     * @param {Event} e
     * @memberof AdvancedFilters
     */
    handleDefaultFilterClick = e => {
        this.resetStateToDefault();
    };

    handleAddKeyValue = field => {
        const keyValueTags = this.state[field];
        const newKeyValueTags = keyValueTags.concat([{key: null, value: null}]);
        this.setState({
            [field]: newKeyValueTags
        });
    };

    handleRemoveKeyValue = (field, index) => {
        const keyValueTags = this.state[field];
        if (keyValueTags.length === 1) {
            const emptyKeyValueTag = [{key: null, value: null}];
            this.setState({
                [field]: emptyKeyValueTag
            });
        } else {
            const newKeyValueTags = R.concat(
                keyValueTags.slice(0, index),
                keyValueTags.slice(index + 1)
            );
            this.setState({
                [field]: newKeyValueTags
            });
        }
    };

    handleKeyChange = (field, index, data) => {
        const keyValueTags = this.state[field];
        keyValueTags[index].key = R.isEmpty(data) ? null : data;
        this.setState({
            [field]: keyValueTags
        });
    };

    handleValueChange = (field, index, data) => {
        const keyValueTags = this.state[field];
        keyValueTags[index].value = R.isEmpty(data) ? null : data;
        this.setState({
            [field]: keyValueTags
        });
    };

    renderGeneratedFilterSuggestions = () => {
        const {generatedFiltersApiUrl, campaignId} = this.props;
        if (generatedFiltersApiUrl && campaignId) {
            return (
                <AutomatedFilterSelect
                    handleFilterSelect={this.handleFilterSelect}
                    filtersApiUrl={generatedFiltersApiUrl}
                    campaignId={campaignId}
                />
            );
        }
    };

    getSelectedBrands = excludeOrIncludeTags => {
        const brandsSelected = R.pipe(
            R.pluck('value'),
            R.filter(tag => tag.startsWith(BLACKLIST_BRAND_TAG_PREFIX)),
            R.map(tag => R.slice(BLACKLIST_BRAND_TAG_PREFIX.length, tag.length, tag)),
            R.map(brandId => ({brandId, name: BRANDS_BY_ID[brandId]}))
        )(excludeOrIncludeTags || []);

        return brandsSelected;
    };

    getExcludeOrIncludeTagExtras = brandTags =>
        R.map(({brandId, name}) => {
            const brandDisplay = name || 'unknown';
            return (
                <span className="selected-brands-identifier-hint" key={brandId}>
                    [{brandId} = {brandDisplay}]
                </span>
            );
        })(brandTags);

    getExludeOrIncludeInputHelper = (excludeOrIncludeTags, field, displayText) => (
        <InputSelect
            className={excludeOrIncludeTags && 'label label-success'}
            clearable={false}
            defaultValue={''}
            header={`${displayText} Tags Brand Helper:`}
            help="Helper to find appropriate brand tags"
            name="tags-brands-helper"
            onChange={brand => {
                const brandTag = `${BLACKLIST_BRAND_TAG_PREFIX}${brand.value}`;
                const tag = {value: brandTag, label: brandTag};
                return this.setState({
                    [field]: R.unionWith((a, b) => a.value === b.value, excludeOrIncludeTags, [tag])
                });
            }}
            options={R.map(brand => ({value: brand.id, label: brand.name}), allBrands)}
            value={''}
        />
    );

    render() {
        const {
            showQueryGuide = true,
            applyFiltersCopy,
            tagStatsUrl,
            campaignFuzzySearchUrl,
            filtersApiUrl,
            flightsApiUrl,
            enums,

            tags = [],
            key_value_tags = [],
            allCampaigns = []
        } = this.props;

        const {
            lucene_query,
            sourced_by,
            last_imported_start_moment,
            last_imported_end_moment,
            include_tags,
            exclude_tags,
            include_key_value_tags_v2,
            exclude_key_value_tags_v2,
            include_content_analysis_tags,
            exclude_content_analysis_tags,
            include_interests,
            exclude_interests,
            has_approved_invite_for_campaigns,
            has_approved_invite_for_flight,
            min_pending_invites,
            max_pending_invites,
            min_non_pending_invites,
            max_non_pending_invites,
            min_invites_sent,
            max_invites_sent,
            min_admin_approved_campaigns,
            max_admin_approved_campaigns,
            min_engagements_per_post,
            max_engagements_per_post,
            min_engagement_rate,
            max_engagement_rate,
            min_comment_engagement_percentage,
            max_comment_engagement_percentage,
            min_avg_invite_score,
            max_avg_invite_score,
            metrics_platform,
            min_followers,
            max_followers,
            min_reference_payout,
            max_reference_payout,
            min_posts_on_account,
            max_posts_on_account,
            profile_country,
            lead_country,
            city,
            region,
            has_no_invite_for_campaigns,
            min_accepted_posts,
            max_accepted_posts,
            min_rejected_posts,
            max_rejected_posts,
            min_avg_pq,
            max_avg_pq,
            min_avg_video_quality,
            max_avg_video_quality,
            has_approved_post_for_campaigns,
            registered,
            has_lead_for_platform,
            has_auth_for_platform,
            showAdvancedOptions
        } = this.state;

        if (R.any(R.isEmpty)([tags, key_value_tags, allCampaigns])) {
            return (
                <div className="layout-center--spinner">
                    <LoadingSpinner additionalClasses="fa-5x" />
                </div>
            );
        }

        const sourcedBySelect = (
            <InputSelect
                className={sourced_by && 'label label-success'}
                name="sourced-by"
                header="Sourced By:"
                help="Email Address of the Original Sourcer"
                multiSelect
                options={R.map(sourcer => ({value: sourcer, label: sourcer}), sourcers)}
                onChange={R.partial(this.handleInputChange, ['sourced_by'])}
                value={sourced_by}
            />
        );

        const lastImportedStartDatePicker = (
            <InputDatePicker
                className={last_imported_start_moment && 'label label-success'}
                header="Date Imported (Start):"
                onChange={this.handleLastImportedStartChange}
                selected={last_imported_start_moment}
            />
        );

        const lastImportedEndDatePicker = (
            <InputDatePicker
                className={last_imported_end_moment && 'label label-success'}
                header="Date Imported (End):"
                onChange={this.handleLastImportedEndChange}
                selected={last_imported_end_moment}
            />
        );

        const includedBrandsSelected = this.getSelectedBrands(include_tags);
        const includeTagsMultiSelect = (
            <div>
                <InputSelect
                    className={include_tags && 'label label-success'}
                    name="include-tags"
                    header="Include Tags:"
                    help="Include all leads who have these CRM Tags associated. All tags specified are OR'd <br/> meaning that if you specify TagA and TagB it will match all leads who have either tag"
                    multiSelect
                    options={R.map(tag => ({value: tag, label: tag}), tags)}
                    onChange={R.partial(this.handleInputChange, ['include_tags'])}
                    value={include_tags}
                    extras={this.getExcludeOrIncludeTagExtras(includedBrandsSelected)}
                />
                {this.getExludeOrIncludeInputHelper(include_tags, 'include_tags', 'Include')}
            </div>
        );

        const excludedBrandsSelected = this.getSelectedBrands(exclude_tags);
        const excludeTagsMultiSelect = (
            <div>
                <InputSelect
                    className={exclude_tags && 'label label-success'}
                    name="exclude-tags"
                    header="Exclude Tags:"
                    help="Exclude all leads who have these CRM Tags associated. All tags specified are OR'd <br/> meaning that if you specify TagA and TagB it will match all leads who have either tag"
                    multiSelect
                    options={R.map(tag => ({value: tag, label: tag}), tags)}
                    onChange={R.partial(this.handleInputChange, ['exclude_tags'])}
                    value={exclude_tags}
                    defaultValue={exclude_tags}
                    extras={this.getExcludeOrIncludeTagExtras(excludedBrandsSelected)}
                />
                {this.getExludeOrIncludeInputHelper(exclude_tags, 'exclude_tags', 'Exclude')}
            </div>
        );

        const includeKeyValueTagsv2MultiSelectInput = () => {
            const shouldShowLabel = R.pipe(
                R.map(tag => tag.key),
                R.any(R.complement(R.isNil)) // are any of them non-nil?
            )(include_key_value_tags_v2);

            const className = shouldShowLabel ? 'label label-success' : null;

            return (
                <MultiSelectInputGroup
                    className={className}
                    name="include_key_value_tags_v2"
                    header="Include Key Value Tags:"
                    help="Add key value pairs to a search"
                    multiSelect={false}
                    options={R.map(tag => ({value: tag, label: tag}), key_value_tags)}
                    onChangeKey={R.partial(this.handleKeyChange, ['include_key_value_tags_v2'])}
                    onChangeValue={R.partial(this.handleValueChange, ['include_key_value_tags_v2'])}
                    onAddKeyValue={R.partial(this.handleAddKeyValue, ['include_key_value_tags_v2'])}
                    onRemoveKeyValue={R.partial(this.handleRemoveKeyValue, [
                        'include_key_value_tags_v2'
                    ])}
                    keyValueTags={include_key_value_tags_v2}
                />
            );
        };

        const excludeKeyValueTagsv2MultiSelectInput = () => {
            const shouldShowLabel = R.pipe(
                R.map(tag => tag.key),
                R.any(R.complement(R.isNil)) // are any of them non-nil?
            )(exclude_key_value_tags_v2);

            const className = shouldShowLabel ? 'label label-success' : null;
            return (
                <MultiSelectInputGroup
                    className={className}
                    name="exclude_key_value_tags_v2"
                    header="Exclude Key Value Tags:"
                    help="Add key value pairs to exclude from a search"
                    multiSelect={false}
                    options={R.map(tag => ({value: tag, label: tag}), key_value_tags)}
                    onChangeKey={R.partial(this.handleKeyChange, ['exclude_key_value_tags_v2'])}
                    onChangeValue={R.partial(this.handleValueChange, ['exclude_key_value_tags_v2'])}
                    onAddKeyValue={R.partial(this.handleAddKeyValue, ['exclude_key_value_tags_v2'])}
                    onRemoveKeyValue={R.partial(this.handleRemoveKeyValue, [
                        'exclude_key_value_tags_v2'
                    ])}
                    keyValueTags={exclude_key_value_tags_v2}
                />
            );
        };

        const includeContentAnalysisMultiSelectInput = () => {
            const shouldShowLabel = R.pipe(
                R.pluck('key'),
                R.any(R.complement(R.isNil)) // are any of them non-nil?
            )(include_content_analysis_tags);

            const className = shouldShowLabel ? 'label label-success' : null;

            return (
                <MultiSelectInputGroup
                    className={className}
                    name="include_content_analysis_tags"
                    header="Include Content Analysis Tag Frequencies Above:"
                    help="Include leads where the frequency of these detections is above a certain frequency defined [0,1].  Choosing multiple tags in a single row will OR those tags"
                    multiSelect
                    options={R.map(tag => ({value: tag, label: tag}), content_analysis_tags)}
                    onChangeKey={R.partial(this.handleKeyChange, ['include_content_analysis_tags'])}
                    onChangeValue={R.partial(this.handleValueChange, [
                        'include_content_analysis_tags'
                    ])}
                    onAddKeyValue={R.partial(this.handleAddKeyValue, [
                        'include_content_analysis_tags'
                    ])}
                    onRemoveKeyValue={R.partial(this.handleRemoveKeyValue, [
                        'include_content_analysis_tags'
                    ])}
                    keyValueTags={include_content_analysis_tags}
                />
            );
        };

        const excludeContentAnalysisMultiSelectInput = () => {
            const shouldShowLabel = R.pipe(
                R.pluck('key'),
                R.any(R.complement(R.isNil)) // are any of them non-nil?
            )(exclude_content_analysis_tags);

            const className = shouldShowLabel ? 'label label-success' : null;

            return (
                <MultiSelectInputGroup
                    className={className}
                    name="exclude_content_analysis_tags"
                    header="Exclude Content Analysis Tag Frequencies Above:"
                    help="Exclude leads where the frequency of these detections is above a certain frequency defined [0,1].  Choosing multiple tags in a single row will OR those tags"
                    multiSelect
                    options={R.map(tag => ({value: tag, label: tag}), content_analysis_tags)}
                    onChangeKey={R.partial(this.handleKeyChange, ['exclude_content_analysis_tags'])}
                    onChangeValue={R.partial(this.handleValueChange, [
                        'exclude_content_analysis_tags'
                    ])}
                    onAddKeyValue={R.partial(this.handleAddKeyValue, [
                        'exclude_content_analysis_tags'
                    ])}
                    onRemoveKeyValue={R.partial(this.handleRemoveKeyValue, [
                        'exclude_content_analysis_tags'
                    ])}
                    keyValueTags={exclude_content_analysis_tags}
                />
            );
        };

        const includeInterestsMultiSelect = (
            <InputSelect
                className={include_interests && 'label label-success'}
                name="include-interests"
                header="Include Interests:"
                help="Include all leads who have these Interests associated. All tags specified are OR'd <br/> meaning that if you specify IntA and IntB it will find all leads who have either interest"
                multiSelect
                options={R.map(interest => ({value: interest, label: interest}), interests)}
                onChange={R.partial(this.handleInputChange, ['include_interests'])}
                value={include_interests}
            />
        );

        const excludeInterestsMultiSelect = (
            <InputSelect
                className={exclude_interests && 'label label-success'}
                name="exclude-interests"
                header="Exclude Interests:"
                help="Exclude all leads who have these Interests associated. All tags specified are OR'd <br/> meaning that if you specify IntA and IntB it will find all leads who have either interest"
                multiSelect
                options={R.map(interest => ({value: interest, label: interest}), interests)}
                onChange={R.partial(this.handleInputChange, ['exclude_interests'])}
                value={exclude_interests}
            />
        );

        const hasInviteForCampaignMultiSelect = (
            <InvertibleInput
                isInverted={this.state.invert_has_approved_invite_for_campaigns}
                name="invert_has_approved_invite_for_campaigns"
                onChange={this.handleCheckboxChange}
                header="Has Approved Invite for Campaign:"
                invertedHeader="Has No Approved Invite for Campaign:"
                help="Include all leads who have an Approved (Admin Approved) Invite for the Selected Campaigns"
                invertedHelp="Do not include leads who have an Approved (Admin Approved) Invite for the Selected Campaigns"
            >
                <InputSelect
                    className={has_approved_invite_for_campaigns && 'label label-success'}
                    name="invite-for-campaign"
                    multiSelect
                    options={R.map(
                        campaign => ({value: campaign.id, label: campaign.name}),
                        allCampaigns
                    )}
                    onChange={R.partial(this.handleInputChange, [
                        'has_approved_invite_for_campaigns'
                    ])}
                    value={has_approved_invite_for_campaigns}
                />
            </InvertibleInput>
        );

        const hasNoInviteForCampaignMultiSelect = (
            <InputSelect
                className={has_no_invite_for_campaigns && 'label label-success'}
                name="no-invite-for-campaigns"
                header="Has No Invite for Campaign(s)"
                help="Include all leads who has no Invite for the Selected Campaigns"
                multiSelect
                options={R.map(
                    campaign => ({value: campaign.id, label: campaign.name}),
                    allCampaigns
                )}
                onChange={R.partial(this.handleInputChange, ['has_no_invite_for_campaigns'])}
                value={has_no_invite_for_campaigns}
            />
        );

        const hasInviteForSentFlightInput = (
            <FlightFinder
                campaignFuzzySearchUrl={campaignFuzzySearchUrl}
                flightsApiUrl={flightsApiUrl}
                onSelect={R.partial(this.handleInputChange, ['has_approved_invite_for_flight'])}
                currentlySelected={has_approved_invite_for_flight}
                enums={enums}
            />
        );

        const numberOfPendingInvitesRange = (
            <InputRange
                className={(min_pending_invites || max_pending_invites) && 'label label-success'}
                header="Number of Pending Invites:"
                help="Include all leads who have within this range of Pending (no action taken by the Creator) Invites."
                onChangeMin={R.partial(this.handleInputChange, ['min_pending_invites'])}
                onChangeMax={R.partial(this.handleInputChange, ['max_pending_invites'])}
                valueMin={min_pending_invites}
                valueMax={max_pending_invites}
            />
        );

        const numberOfNonPendingInvitesRange = (
            <InputRange
                className={
                    (min_non_pending_invites || max_non_pending_invites) && 'label label-success'
                }
                header="Number of Non-Pending Invites:"
                help="Include all leads who have within this range of Non-Pending (action taken by the Creator) Invites."
                onChangeMin={R.partial(this.handleInputChange, ['min_non_pending_invites'])}
                onChangeMax={R.partial(this.handleInputChange, ['max_non_pending_invites'])}
                valueMin={min_non_pending_invites}
                valueMax={max_non_pending_invites}
            />
        );

        const numberOfInvitesSentRange = (
            <InputRange
                className={(min_invites_sent || max_invites_sent) && 'label label-success'}
                header="Total number of invites sent:"
                help="Include all leads who have within this range of invites to campaigns sent to them."
                onChangeMin={R.partial(this.handleInputChange, ['min_invites_sent'])}
                onChangeMax={R.partial(this.handleInputChange, ['max_invites_sent'])}
                valueMin={min_invites_sent}
                valueMax={max_invites_sent}
            />
        );

        const numberOfAdminApprovedCampaignsRange = (
            <InputRange
                className={
                    (min_admin_approved_campaigns || max_admin_approved_campaigns) &&
                    'label label-success'
                }
                header="Total number of admin approved campaigns:"
                help="Include all leads who have within this range of invites to campaigns sent to them approved by an admin."
                onChangeMin={R.partial(this.handleInputChange, ['min_admin_approved_campaigns'])}
                onChangeMax={R.partial(this.handleInputChange, ['max_admin_approved_campaigns'])}
                valueMin={min_admin_approved_campaigns}
                valueMax={max_admin_approved_campaigns}
            />
        );

        const metricsPlatformSelect = (
            <InputSelect
                className={metrics_platform && 'label label-success'}
                name="metrics_platform"
                header="Metrics Platform"
                help="Choose which Social Platform to base the Follower Filter and/or the Reference Payout Filter On <br/> If no platform is selected then ALL platforms will be considered."
                multiSelect={false}
                options={R.map(
                    platform => ({value: platform.value, label: platform.text}),
                    metricsSearchableCampaignTypes
                )}
                onChange={R.partial(this.handleInputChange, ['metrics_platform'])}
                value={metrics_platform}
            />
        );

        const numberOfFollowersRange = (
            <InputRange
                className={(min_followers || max_followers) && 'label label-success'}
                header="Number of Followers:"
                help="Include all leads who have within this range of Followers. <br/> If a Metrics Platform is selected then we will consider only followers on that Social Platform <br/> If no Metrics Platform is selected then ALL social Platforms will be considered"
                onChangeMin={R.partial(this.handleInputChange, ['min_followers'])}
                onChangeMax={R.partial(this.handleInputChange, ['max_followers'])}
                valueMin={min_followers}
                valueMax={max_followers}
            />
        );

        const referencePayoutAmountRange = (
            <InputRange
                className={(min_reference_payout || max_reference_payout) && 'label label-success'}
                header="Reference Payout Amount (USD):"
                help="Include all leads who have within this range of Reference Payout. <br/> If a Metrics Platform is selected then we will consider only Reference Payout on that Social Platform <br/> If no Metrics Platform is selected then ALL social Platforms will be considered"
                onChangeMin={R.partial(this.handleInputChange, ['min_reference_payout'])}
                onChangeMax={R.partial(this.handleInputChange, ['max_reference_payout'])}
                valueMin={min_reference_payout}
                valueMax={max_reference_payout}
            />
        );

        const engagementsPerPostRange = (
            <InputRange
                className={
                    (min_engagements_per_post || max_engagements_per_post) && 'label label-success'
                }
                header="Engagements Per Post:"
                help="Include all leads who have within this range of Engagements Per Post.  <br/> If a Metrics Platform is selected then we will consider only Engagements Per Post on that Social Platform <br/> If no Metrics Platform is selected then ALL social Platforms will be considered"
                onChangeMin={R.partial(this.handleInputChange, ['min_engagements_per_post'])}
                onChangeMax={R.partial(this.handleInputChange, ['max_engagements_per_post'])}
                valueMin={min_engagements_per_post}
                valueMax={max_engagements_per_post}
            />
        );

        const engagementRateRange = (
            <InputRange
                className={(min_engagement_rate || max_engagement_rate) && 'label label-success'}
                header="Engagement Rate:"
                help="Include all leads who have within this range of Engagement Rate.  <br/> If a Metrics Platform is selected then we will consider only Engagement Rate on that Social Platform <br/> If no Metrics Platform is selected then ALL social Platforms will be considered"
                onChangeMin={R.partial(this.handleInputChange, ['min_engagement_rate'])}
                onChangeMax={R.partial(this.handleInputChange, ['max_engagement_rate'])}
                valueMin={min_engagement_rate}
                valueMax={max_engagement_rate}
            />
        );

        const commentEngagementPercentageRange = (
            <InputRange
                className={
                    (min_comment_engagement_percentage || max_comment_engagement_percentage) &&
                    'label label-success'
                }
                header="Comment Engagement Percentage (%):"
                help="(Comments / (Likes + Comments)) * 100"
                onChangeMin={R.partial(this.handleInputChange, [
                    'min_comment_engagement_percentage'
                ])}
                onChangeMax={R.partial(this.handleInputChange, [
                    'max_comment_engagement_percentage'
                ])}
                valueMin={min_comment_engagement_percentage}
                valueMax={max_comment_engagement_percentage}
            />
        );

        const inviteScoreRange = (
            <InputRange
                className={(min_avg_invite_score || max_avg_invite_score) && 'label label-success'}
                header="Average Creator Invite Score (1-5)"
                help="Average score provided by ops."
                onChangeMin={R.partial(this.handleInputChange, ['min_avg_invite_score'])}
                onChangeMax={R.partial(this.handleInputChange, ['max_avg_invite_score'])}
                valueMin={min_avg_invite_score}
                valueMax={max_avg_invite_score}
            />
        );

        const contentTypeSpecialtyMultiSelect = (
            <InvertibleInput
                isInverted={this.state.invert_content_type_specialty}
                name="invert_content_type_specialty"
                onChange={this.handleCheckboxChange}
                header="Content Type Specialty (self-identified)"
                invertedHeader="Exclude Content Type Specialty (self-identified)"
                help="Include all leads who have identified themselves as having this Content Specialty"
                invertedHelp="Exclude all leads who have identified themselves as having this Content Specialty"
            >
                <InputSelect
                    className={this.state.content_type_specialty && 'label label-success'}
                    name="content-type-specialty"
                    multiSelect
                    options={R.map(
                        specialty => ({value: specialty.value, label: specialty.text}),
                        contentTypeSpecialties
                    )}
                    onChange={R.partial(this.handleInputChange, ['content_type_specialty'])}
                    value={this.state.content_type_specialty}
                />
            </InvertibleInput>
        );

        const primaryLanguageMultiSelect = (
            <InvertibleInput
                isInverted={this.state.invert_language}
                name="invert_language"
                onChange={this.handleCheckboxChange}
                header="Primary Language:"
                invertedHeader="Exclude Primary Language:"
                help="Include all leads who have any of the selected languages as their Primary Language"
                invertedHelp="Exclude all leads who have any of the selected languages as their Primary Language"
            >
                <InputSelect
                    className={this.state.language && 'label label-success'}
                    name="primary-language"
                    multiSelect
                    options={R.map(
                        language => ({value: language.code, label: language.name}),
                        languages
                    )}
                    onChange={R.partial(this.handleInputChange, ['language'])}
                    value={this.state.language}
                />
            </InvertibleInput>
        );

        const crawlCountryMultiSelect = (
            <InvertibleInput
                isInverted={this.state.invert_lead_country}
                name="invert_lead_country"
                onChange={this.handleCheckboxChange}
                header="Crawl Country:"
                invertedHeader="Exclude Crawl Country:"
                help="Include all leads who have any of the selected countries as their Lead Crawl Country"
                invertedHelp="Exclude all leads who have any of the selected countries as their Lead Crawl Country"
            >
                <InputSelect
                    className={lead_country && 'label label-success'}
                    name="crawl-country"
                    multiSelect
                    options={R.map(
                        country => ({value: country.code, label: country.name}),
                        countries
                    )}
                    onChange={R.partial(this.handleInputChange, ['lead_country'])}
                    value={lead_country}
                />
            </InvertibleInput>
        );

        const profileCountryMultiSelect = (
            <InvertibleInput
                isInverted={this.state.invert_profile_country}
                name="invert_profile_country"
                onChange={this.handleCheckboxChange}
                header="Profile Country:"
                invertedHeader="Exclude Profile Country:"
                help="Include all leads who have any of the selected countries set as their User Profile Country"
                invertedHelp="Exclude all leads who have any of the selected countries set as their User Profile Country"
            >
                <InputSelect
                    className={profile_country && 'label label-success'}
                    name="profile-country"
                    multiSelect
                    options={R.map(
                        country => ({value: country.code, label: country.name}),
                        countries
                    )}
                    onChange={R.partial(this.handleInputChange, ['profile_country'])}
                    value={profile_country}
                />
            </InvertibleInput>
        );

        const cityInput = (
            <InputTags
                className={!R.isNil(city) && !R.isEmpty(city) && 'label label-success'}
                name="city"
                header="Creator City (hit Enter after each city name)"
                help="Include all leads who have any of the specified Cities as their Inferred City or User Profile City (User Input)"
                value={!R.isNil(city) ? R.split(',', city) : []}
                onChange={R.partial(this.handleInputChange, ['city'])}
            />
        );

        const regionInput = (
            <InputSelect
                className={region && 'label label-success'}
                name="region"
                header="Creator State"
                help="Include all leads who have any of the specified States as their Normalized Region (Crawled) or User Profile State (User Input)"
                multiSelect
                options={R.map(region => ({value: region.code, label: region.name}), regions)}
                onChange={R.partial(this.handleInputChange, ['region'])}
                value={region}
            />
        );

        const latitudeInput = (
            <InputNumber
                className={this.state.latitude && 'label label-success'}
                header="Latitude:"
                help="The latitude of the center point of the circle within which to search"
                onChange={R.partial(this.handleInputChange, ['latitude'])}
                value={this.state.latitude}
            />
        );

        const longitudeInput = (
            <InputNumber
                className={this.state.longitude && 'label label-success'}
                header="Longitude:"
                help="The longitude of the center point of the circle within which to search"
                onChange={R.partial(this.handleInputChange, ['longitude'])}
                value={this.state.longitude}
            />
        );

        const distanceMilesInput = (
            <InputNumber
                className={this.state.distance_miles && 'label label-success'}
                header="Radius (miles):"
                help="The radius of the circle within which to search"
                onChange={R.partial(this.handleInputChange, ['distance_miles'])}
                value={this.state.distance_miles}
            />
        );

        const hasLeadForPlatformFilter = (
            <InputSelect
                className={has_lead_for_platform && 'label label-success'}
                name="has_lead_for_platform"
                header="Has lead for Platform (OR):"
                help="Include all leads who have Lead Metadata for any of the selected Social Platforms"
                multiSelect
                options={R.map(
                    platform => ({value: platform.value, label: platform.text}),
                    campaignTypes
                )}
                onChange={R.partial(this.handleInputChange, ['has_lead_for_platform'])}
                value={has_lead_for_platform}
            />
        );

        const hasAuthForPlatformFilter = (
            <InputSelect
                className={has_auth_for_platform && 'label label-success'}
                name="has_auth_for_platform"
                header="Has auth for Platform (OR):"
                help="Include all leads who have Users who have Authed on any of the selected Social Platforms"
                multiSelect
                options={R.map(
                    platform => ({value: platform.value, label: platform.text}),
                    campaignTypes
                )}
                onChange={R.partial(this.handleInputChange, ['has_auth_for_platform'])}
                value={has_auth_for_platform}
            />
        );

        const publisherTypeMultiSelect = (
            <InvertibleInput
                isInverted={this.state.invert_publisher_type}
                name="invert_publisher_type"
                onChange={this.handleCheckboxChange}
                header="Publisher Type:"
                invertedHeader="Exclude Publisher Type:"
                help="Include all leads who fall under any of the selected Publisher Types either from their Lead (deduced) or their User Profile (User Input)"
                invertedHelp="Exclude all leads who fall under any of the selected Publisher Types either from their Lead (deduced) or their User Profile (User Input)"
            >
                <InputSelect
                    className={this.state.publisher_type && 'label label-success'}
                    name="publisher-type"
                    multiSelect
                    options={R.map(
                        publisherType => ({value: publisherType.value, label: publisherType.text}),
                        publisherTypes
                    )}
                    onChange={R.partial(this.handleInputChange, ['publisher_type'])}
                    value={this.state.publisher_type}
                />
            </InvertibleInput>
        );

        const ageRangeMultiSelect = (
            <InvertibleInput
                isInverted={this.state.invert_age_range}
                name="invert_age_range"
                onChange={this.handleCheckboxChange}
                header="Age Range:"
                invertedHeader="Exclude Age Range:"
                help="Include all leads who fall under any of the given Age Ranges either from their Lead (deduced) or their User Profile (User Input)"
                invertedHelp="Exclude all leads who fall under any of the given Age Ranges either from their Lead (deduced) or their User Profile (User Input)"
            >
                <InputRange
                    className={
                        (this.state.age_range_min || this.state.age_range_max) &&
                        'label label-success'
                    }
                    name="age-range"
                    onChangeMin={R.partial(this.handleInputChange, ['age_range_min'])}
                    onChangeMax={R.partial(this.handleInputChange, ['age_range_max'])}
                    valueMin={this.state.age_range_min}
                    valueMax={this.state.age_range_max}
                />
            </InvertibleInput>
        );

        const isParentMultiSelect = (
            <InputSelect
                className={this.state.is_parent && 'label label-success'}
                name="is-parent"
                header="Creator is a parent"
                help="Include all leads who identified themselves as being or not being a parent in their User Profile (User Input)"
                multiSelect={false}
                options={[
                    {value: 'yes', label: 'yes'},
                    {value: 'no', label: 'no'}
                ]}
                onChange={R.partial(this.handleInputChange, ['is_parent'])}
                value={this.state.is_parent}
            />
        );

        const childrenAgeRangeMultiSelect = (
            <InputRange
                className={
                    (this.state.children_age_range_min || this.state.children_age_range_max) &&
                    'label label-success'
                }
                name="children-age-range"
                header="Children Age Range:"
                help="Include all leads who have a child / children who have an age computed from the given year_born in their User Profile (User Input)"
                onChangeMin={R.partial(this.handleInputChange, ['children_age_range_min'])}
                onChangeMax={R.partial(this.handleInputChange, ['children_age_range_max'])}
                valueMin={this.state.children_age_range_min}
                valueMax={this.state.children_age_range_max}
            />
        );

        const totalAcceptedPostsRange = (
            <InputRange
                className={(min_accepted_posts || max_accepted_posts) && 'label label-success'}
                header="Total Accepted Posts:"
                help="Include all leads who have within this range of Accepted (Admin Accepted) Posts (from any Social Platform) <br/> Does NOT include Content Submission."
                onChangeMin={R.partial(this.handleInputChange, ['min_accepted_posts'])}
                onChangeMax={R.partial(this.handleInputChange, ['max_accepted_posts'])}
                valueMin={min_accepted_posts}
                valueMax={max_accepted_posts}
            />
        );

        const totalRejectedPostsRange = (
            <InputRange
                className={(min_rejected_posts || max_rejected_posts) && 'label label-success'}
                header="Total Rejected Posts:"
                help="Include all leads who have within this range of Rejected (Admin Rejected) Posts (from any Social Platform) <br/> Does NOT include Content Submission."
                onChangeMin={R.partial(this.handleInputChange, ['min_rejected_posts'])}
                onChangeMax={R.partial(this.handleInputChange, ['max_rejected_posts'])}
                valueMin={min_rejected_posts}
                valueMax={max_rejected_posts}
            />
        );

        const averagePostQualityRange = (
            <InputRange
                className={(min_avg_pq || max_avg_pq) && 'label label-success'}
                header="Average Post Quality (1.0 to 5.0):"
                help="Include all leads who have within this range of Average Post Quality"
                onChangeMin={R.partial(this.handleInputChange, ['min_avg_pq'])}
                onChangeMax={R.partial(this.handleInputChange, ['max_avg_pq'])}
                valueMin={min_avg_pq}
                valueMax={max_avg_pq}
            />
        );

        const averageVideoPostQualityRange = (
            <InputRange
                className={
                    (min_avg_video_quality || max_avg_video_quality) && 'label label-success'
                }
                header="Average Video Post Quality (1.0 to 5.0):"
                help="Include all leads who have video posts within this quality range"
                onChangeMin={R.partial(this.handleInputChange, ['min_avg_video_quality'])}
                onChangeMax={R.partial(this.handleInputChange, ['max_avg_video_quality'])}
                valueMin={min_avg_video_quality}
                valueMax={max_avg_video_quality}
            />
        );

        const totalPostsOnAccountRange = (
            <InputRange
                className={(min_posts_on_account || max_posts_on_account) && 'label label-success'}
                header="Total Posts on Account"
                help="Include all leads who have within this range of submitted Posts"
                onChangeMin={R.partial(this.handleInputChange, ['min_posts_on_account'])}
                onChangeMax={R.partial(this.handleInputChange, ['max_posts_on_account'])}
                valueMin={min_posts_on_account}
                valueMax={max_posts_on_account}
            />
        );

        const hasApprovedPostForCampaignMultiSelect = (
            <InputSelect
                className={has_approved_post_for_campaigns && 'label label-success'}
                name="post-for-campaign"
                header="Has Approved Post for Campaign:"
                help="Include all leads who have an Approved (Admin Approved) Post for any of the Specified Campaigns"
                multiSelect
                options={R.map(
                    campaign => ({value: campaign.id, label: campaign.name}),
                    allCampaigns
                )}
                onChange={R.partial(this.handleInputChange, ['has_approved_post_for_campaigns'])}
                value={has_approved_post_for_campaigns}
            />
        );

        const registrationStatusMultiSelect = (
            <InputSelect
                className={registered && 'label label-success'}
                name="registration"
                header="Registration Status:"
                help="Include all leads who either are registered (have a User account) or are unregistered (have no User account)"
                multiSelect={false}
                options={registrationStatuses}
                onChange={R.partial(this.handleInputChange, ['registered'])}
                value={registered}
            />
        );

        const demographicsPublisherTypesMultiSelect = (
            <InputSelect
                className={this.state.demographics_publisher_types && 'label label-success'}
                name="demographics-publisher-types"
                header="Creator Type(s):"
                help="Fuzzily target the specified publisher types with this search."
                multiSelect
                options={R.map(
                    publisherType => ({value: publisherType.value, label: publisherType.text}),
                    publisherTypes
                )}
                onChange={R.partial(this.handleInputChange, ['demographics_publisher_types'])}
                value={this.state.demographics_publisher_types}
            />
        );

        const confidenceHelp = function(relatedFieldHeader, relatedFieldName, relatedFieldExample) {
            return `This works in conjunction with the "${relatedFieldHeader}" input.
                    Confidence percentage ranges from 0 to 100.
                    Human tagged data has a confidence of 85%.
                    User provided data (via profile) has a confidence of 100%.
                    Machine tagged data has a confidence between 50% and 100%
                    depending on how well the lead matches the stereotypes of the given ${relatedFieldName}.
                    For example, if you were to set the "${relatedFieldHeader}" filter to "${relatedFieldExample}"
                    and this input to "80", you would retrieve all leads that are marked as ${relatedFieldExample} in their profile,
                    those that don't have profile data but were tagged as ${relatedFieldExample} by a human,
                    and those that don't have profile or human tagged data
                    but were tagged as ${relatedFieldExample} by a machine with 80% confidence or better.`;
        };

        const demographicsPublisherTypesConfidenceInput = (
            <InputNumber
                className={
                    this.state.demographics_publisher_types_confidence && 'label label-success'
                }
                header="Minimum Creator Type(s) Confidence Percentage:"
                help={confidenceHelp('Creator Type(s)', 'creator type', 'Male')}
                onChange={R.partial(this.handleInputChange, [
                    'demographics_publisher_types_confidence'
                ])}
                value={this.state.demographics_publisher_types_confidence}
            />
        );

        const demographicsCountriesMultiSelect = (
            <InputSelect
                className={this.state.demographics_countries && 'label label-success'}
                name="demographics-countries"
                header="Countries"
                help="Fuzzily target the specified countries with this search"
                multiSelect
                options={R.map(country => ({value: country.code, label: country.name}), countries)}
                onChange={R.partial(this.handleInputChange, ['demographics_countries'])}
                value={this.state.demographics_countries}
            />
        );

        const loadCityOptions = debounce(function(input, callback) {
            if (R.isEmpty(input)) {
                return callback(null, {options: []});
            }
            return axios.get(sn.urls.citiesUrl, {params: {query: input}}).then(({data}) => {
                const options = R.map(({id, name}) => ({value: id, label: name}), data);
                callback(null, {options});
            });
        }, 500);
        const demographicsCitiesMultiSelect = (
            <InputSelect
                className={this.state.demographics_cities && 'label label-success'}
                name="demographics-cities"
                header="Cities"
                help="Fuzzily target the specified cities with this search"
                multiSelect
                loadOptions={loadCityOptions}
                onChange={R.partial(this.handleInputChange, ['demographics_cities'])}
                value={this.state.demographics_cities}
            />
        );

        const demographicsIncludeInterestsMultiSelect = (
            <InputSelect
                className={this.state.demographics_include_interests && 'label label-success'}
                name="demographics-include-interests"
                header="Include Interests"
                help="Fuzzily target the specified user interests with this search"
                multiSelect
                options={R.map(interest => ({value: interest, label: interest}), interests)}
                onChange={R.partial(this.handleInputChange, ['demographics_include_interests'])}
                value={this.state.demographics_include_interests}
            />
        );

        const demographicsIncludeInterestsConfidenceInput = (
            <InputNumber
                className={
                    this.state.demographics_include_interests_confidence && 'label label-success'
                }
                header="Minimum Interests Confidence Percentage:"
                help={confidenceHelp('Include Interests', 'interest', 'Art/Design')}
                onChange={R.partial(this.handleInputChange, [
                    'demographics_include_interests_confidence'
                ])}
                value={this.state.demographics_include_interests_confidence}
            />
        );

        const scoringStrategySelect = (
            <InputSelect
                className={this.state.scoring_strategy && 'label label-success'}
                name="scoring_strategy"
                header="Scoring Strategy:"
                help="Choose how you would like results to be sorted"
                multiSelect={false}
                clearable={false}
                options={R.map(
                    strategy => ({value: strategy.value, label: strategy.text}),
                    scoringStrategies
                )}
                onChange={R.partial(this.handleInputChange, ['scoring_strategy'])}
                value={this.state.scoring_strategy}
            />
        );

        const brandScoringSelect = (
            <InputSelect
                className={this.state.boost_creators_for_brand_id && 'label label-success'}
                name="boost_creators_for_brand_id"
                header="Prefer Creators for This Brand"
                help="Select a brand to boost creators that have highly-rated content for this brand"
                multiSelect={false}
                clearable
                options={R.map(brand => ({value: brand.id, label: brand.name}), allBrands)}
                onChange={R.partial(this.handleInputChange, ['boost_creators_for_brand_id'])}
                value={this.state.boost_creators_for_brand_id}
            />
        );

        const hasPhoneNumberCheckbox = (
            <Checkbox
                className={this.state.has_phone_number && 'label label-success'}
                label="Only show leads with phone numbers"
                name="show-leads-with-phone-numbers"
                help="Toggle to show only leads who have a phone number associated with their profile"
                checked={this.state.has_phone_number}
                onChange={R.partial(this.handleCheckboxChange, ['has_phone_number'])}
            />
        );

        const hasCompleteProfileCheckbox = (
            <Checkbox
                className={this.state.has_complete_profile && 'label label-success'}
                label="Show leads with complete profiles"
                name="show-leads-with-complete-profile"
                help="Toggle to show only leads who have a complete profile"
                checked={this.state.has_complete_profile}
                onChange={R.partial(this.handleCheckboxChange, ['has_complete_profile'])}
            />
        );

        const hasIncompleteProfileCheckbox = (
            <Checkbox
                className={this.state.has_incomplete_profile && 'label label-success'}
                label="Show leads with incomplete profiles"
                name="show-leads-with-incomplete-profiles"
                help="Toggle to show only leads who have an incomplete profile"
                checked={this.state.has_incomplete_profile}
                onChange={R.partial(this.handleCheckboxChange, ['has_incomplete_profile'])}
            />
        );

        const avgViewsPerVideoRange = (
            <InputRange
                className={
                    (this.state.avg_views_per_video_min || this.state.avg_views_per_video_max) &&
                    'label label-success'
                }
                name="avg-views-per-video"
                header="Average Views Per Video:"
                help="Include all Instagram leads whose videos average views within this range (sampled over videos within their most recent 20 posts). (total video views / number of videos)"
                onChangeMin={R.partial(this.handleInputChange, ['avg_views_per_video_min'])}
                onChangeMax={R.partial(this.handleInputChange, ['avg_views_per_video_max'])}
                valueMin={this.state.avg_views_per_video_min}
                valueMax={this.state.avg_views_per_video_max}
            />
        );

        const recentVideoPercentRange = (
            <InputRange
                className={
                    (this.state.recent_video_percent_min || this.state.recent_video_percent_max) &&
                    'label label-success'
                }
                name="recent-video-percent"
                header="Recent Video Percent:"
                help="Include all Instagram leads whose ratio of video to total posts is within this range (sampled over their most recent 20 posts). (number of videos / number of any posts)"
                onChangeMin={R.partial(this.handleInputChange, ['recent_video_percent_min'])}
                onChangeMax={R.partial(this.handleInputChange, ['recent_video_percent_max'])}
                valueMin={this.state.recent_video_percent_min}
                valueMax={this.state.recent_video_percent_max}
            />
        );

        const recentVideoViewsRange = (
            <InputRange
                className={
                    (this.state.recent_video_views_min || this.state.recent_video_views_max) &&
                    'label label-success'
                }
                name="recent-video-views"
                header="Recent Video Views:"
                help="Include all leads whose recent videos have total views within this range (totaled over videos in their most recent 20 posts)."
                onChangeMin={R.partial(this.handleInputChange, ['recent_video_views_min'])}
                onChangeMax={R.partial(this.handleInputChange, ['recent_video_views_max'])}
                valueMin={this.state.recent_video_views_min}
                valueMax={this.state.recent_video_views_max}
            />
        );

        const trueVideoEngagementRateRange = (
            <InputRange
                className={
                    (this.state.true_video_engagement_rate_min ||
                        this.state.true_video_engagement_rate_max) &&
                    'label label-success'
                }
                name="true-video-engagement-rate"
                header="True Video Engagement Rate:"
                help="Include all leads whose ratio of video comments and likes to video views is within this range (sampled over their most recent 20 posts). ((number of video comments + number of video likes) / (number of video views)"
                onChangeMin={R.partial(this.handleInputChange, ['true_video_engagement_rate_min'])}
                onChangeMax={R.partial(this.handleInputChange, ['true_video_engagement_rate_max'])}
                valueMin={this.state.true_video_engagement_rate_min}
                valueMax={this.state.true_video_engagement_rate_max}
            />
        );

        const videoViewRateRange = (
            <InputRange
                className={
                    (this.state.video_view_rate_min || this.state.video_view_rate_max) &&
                    'label label-success'
                }
                name="video-view-rate"
                header="Video View Rate:"
                help="Include all leads whose ratio of video views to followers falls within this range (sampled over their most recent 20 posts). (number of video views / followers)"
                onChangeMin={R.partial(this.handleInputChange, ['video_view_rate_min'])}
                onChangeMax={R.partial(this.handleInputChange, ['video_view_rate_max'])}
                valueMin={this.state.video_view_rate_min}
                valueMax={this.state.video_view_rate_max}
            />
        );

        const videoEngagementRateRange = (
            <InputRange
                className={
                    (this.state.video_engagement_rate_min ||
                        this.state.video_engagement_rate_max) &&
                    'label label-success'
                }
                name="video-engagement-rate"
                header="Video Engagement Rate:"
                help="Include all leads whose ratio of video comments and likes to followers falls within this range (sampled over their most recent 20 posts). ((number of video comments + number of video likes) / follewers)"
                onChangeMin={R.partial(this.handleInputChange, ['video_engagement_rate_min'])}
                onChangeMax={R.partial(this.handleInputChange, ['video_engagement_rate_max'])}
                valueMin={this.state.video_engagement_rate_min}
                valueMax={this.state.video_engagement_rate_max}
            />
        );

        const applyFilterButton = (
            <Button
                text={applyFiltersCopy || 'Apply Filter'}
                classes="btn btn-primary"
                onClick={this.handleApplyFilterClick}
            />
        );

        const clearFilterButton = (
            <Button
                text="Clear Filter"
                classes="btn btn-warning"
                onClick={this.handleClearFilterClick}
            />
        );

        const defaultFilterButton = (
            <Button
                text="Default Filter"
                classes="btn btn-info"
                onClick={this.handleDefaultFilterClick}
            />
        );

        const maxNewResultsInput = (
            <InputNumber
                className={this.state.max_new_results && 'label label-success'}
                header="Max new results:"
                help="Limit the new creator results added to the lead hopper to the specified number"
                onChange={R.partial(this.handleInputChange, ['max_new_results'])}
                value={this.state.max_new_results}
            />
        );

        const maxReactivatedResultsInput = (
            <InputNumber
                className={this.state.max_reactivated_results && 'label label-success'}
                header="Max reactivated results:"
                help="Limit the reactivated creator results added to the lead hopper to the specified number"
                onChange={R.partial(this.handleInputChange, ['max_reactivated_results'])}
                value={this.state.max_reactivated_results}
            />
        );

        const minScoreInput = (
            <InputNumber
                className={this.state.min_score && 'label label-success'}
                header="Minimum Secret Sauce Score:"
                help="Limit the results returned to those with a score greater than or equal to the specified number"
                onChange={R.partial(this.handleInputChange, ['min_score'])}
                value={this.state.min_score}
            />
        );

        const actionButtons = (
            <div id="action-buttons">
                <div className="panel-heading filter-heading">Actions</div>
                <div className="btn-group">
                    {applyFilterButton}
                    {defaultFilterButton}
                    {clearFilterButton}
                </div>
            </div>
        );

        const optionalFilters = (
            <div className="misc-panel">
                <div className="panel-heading filter-heading">Misc. Filters</div>
                <div className="row">
                    <div className="col-xs-12">{registrationStatusMultiSelect}</div>
                </div>
            </div>
        );

        const tagsLink = tagStatsUrl ? (
            <div className="form-group">
                <a href={tagStatsUrl}>
                    <button className="btn btn-default">View Tags Breakdown</button>
                </a>
            </div>
        ) : null;

        const queryField = (
            <div>
                <Input
                    className={lucene_query && 'label label-success'}
                    header="Lucene Query"
                    help="Advanced Lucene Query"
                    onChange={R.partial(this.handleInputChange, ['lucene_query'])}
                    value={lucene_query}
                />
                {showQueryGuide ? (
                    <ExpandablePanel header="Query Guide" expanded={false}>
                        <div>
                            <div className="text-center">
                                <a href="https://docs.google.com/a/socialnative.com/document/d/16FxlQ7DcQYIZbRdZX-t_Mm6W6PHyoBOcpk_Ou0vmygQ/edit?usp=sharing">
                                    Ops Self-Help Query Guide <i className="fa fa-external-link" />
                                </a>
                            </div>
                            <hr />
                            <b>
                                Note: if no field is specified using the &quot;field:value&quot;
                                syntax, the query will be run against the following fields:
                                <ul>
                                    <li>email^50</li>
                                    <li>instagram.social_handle^50</li>
                                    <li>youtube.social_handle^50</li>
                                    <li>bio^25</li>
                                    <li>super_caption^10</li>
                                </ul>
                                The &quot;^&quot; after the field name means that the field is
                                boosted. For example, in this case email and the social handles are
                                weighted twice as much and the bio, and 5 times as much and the
                                super_caption.
                            </b>
                            <br />
                            <br />
                            <dl>
                                <dt>fish</dt>
                                <dd>searches for keyword fish</dd>
                                <dt>fish AND boat</dt>
                                <dd>results contain both fish and boat</dd>
                                <dt>fish OR boat</dt>
                                <dd>results contain either fish or boat or both</dd>
                                <dt>{'-fish'}</dt>
                                <dd>
                                    {
                                        'excludes fish from search results, format -term or -"two term"'
                                    }
                                </dd>
                                <dt>{'fish*'}</dt>
                                <dd>wildcard search, includes fish, fishes, fishing, etc...</dd>
                            </dl>
                            <dl>
                                <dt>fish boat</dt>
                                <dd>identical to fish OR boat</dd>
                                <dt>(fish OR boat) AND sea</dt>
                                <dd>
                                    {' '}
                                    use parentheses to define grouping. results contain sea and
                                    either fish or boat
                                </dd>
                                <dt>{'"fish boat"'}</dt>
                                <dd>results contain the entire phrase &quot;fish boat&quot;</dd>
                                <dt>fish boat</dt>
                                <dd>identical to fish OR boat</dd>
                                <dt>(fish OR boat) AND sea</dt>
                                <dd>
                                    {' '}
                                    use parentheses to define grouping. results contain sea and
                                    either fish or boat
                                </dd>
                                <dt>{'"fish boat"'}</dt>
                                <dd>results contain the entire phrase &quot;fish boat&quot;</dd>
                            </dl>
                            <label>
                                <big>Field Search and Boosting</big>
                            </label>
                            <p>You can search a field by using the following syntax:</p>
                            <dl>
                                <dt>{'username:abc'}</dt>
                                <dd>
                                    {'searches a specific field for a value, format field:value'}
                                    <br />
                                    Note that if you specify a term using the field:value format,
                                    that value will only be searched for in that field and none of
                                    the default fields from above. If you provide another value in
                                    the query that does not use the field:value syntax, then that
                                    value will be searched for in the default fields from above.
                                </dd>
                                <dt>{'username:abc^2 AND bio:def'}</dt>
                                <dd>
                                    This is an example of boosting. This query will search for leads
                                    that have &apos;abc&apos; in their username and &apos;def&apos;
                                    in their bio. The boosting operator (^) affects the relevance of
                                    a term. You can specify any positive number. If you choose a
                                    number between 0 and 1, you reduce the relevance of a term
                                    compared to other terms in the query. If you choose a number
                                    greater than 1, you increase the relevance. In the above
                                    example, a match in the username is twice as relevent as a match
                                    in the bio.
                                </dd>
                            </dl>
                            <dl>
                                <dt>Possible Search Fields</dt>
                                <dd>
                                    {`
                                        "id",
                                        "email",
                                        "language",
                                        "country",
                                        "tags",
                                        "user.id",
                                        "user.email",
                                        "user.created_at",
                                        "user.interest_tags",
                                        "user_profile.id",
                                        "user_profile.name",
                                        "user_profile.address",
                                        "user_profile.city",
                                        "user_profile.state",
                                        "user_profile.zip_code",
                                        "user_profile.country",
                                        "user_profile.phone_number",
                                        "user_profile.paypal_email",
                                        "user_profile.gender",
                                        "user_profile.age",
                                        "user_profile.children",
                                        "user_profile.age_range",
                                        "user_profile.publisher_type",
                                        "user_profile.content_type_specialty",
                                        "user_profile.contact_preference_text",
                                        "instagram.avg_comments_per_post",
                                        "instagram.avg_comment_rate",
                                        "instagram.avg_likes_per_post",
                                        "instagram.avg_like_rate",
                                        "instagram.avg_engagements_per_post",
                                        "instagram.avg_engagement_rate",
                                        "instagram.follows",
                                        "instagram.followed_by",
                                        "instagram.publisher_rank",
                                        "instagram.reference_payout",
                                        "instagram.total_posts",
                                        "instagram.id",
                                        "instagram.social_handle",
                                        "instagram.social_id",
                                        "instagram.age_range",
                                        "instagram.publisher_type",
                                        "instagram.min_compensation",
                                        "instagram.recent_medial_urls",
                                        "instagram.tags",
                                        "instagram.super_caption",
                                        "instagram.sourced_by",
                                        "instagram.last_imported_at",
                                        "youtube.avg_comments_per_post",
                                        "youtube.avg_comment_rate",
                                        "youtube.avg_likes_per_post",
                                        "youtube.avg_like_rate",
                                        "youtube.avg_dislikes_per_post",
                                        "youtube.avg_dislike_rate",
                                        "youtube.avg_views_per_post",
                                        "youtube.avg_view_rate",
                                        "youtube.avg_favorites_per_post",
                                        "youtube.avg_favorite_rate",
                                        "youtube.avg_engagements_per_post",
                                        "youtube.avg_engagement_rate",
                                        "youtube.subscribers",
                                        "youtube.total_posts",
                                        "youtube.publisher_rank",
                                        "youtube.reference_payout",
                                        "youtube.id",
                                        "youtube.social_handle",
                                        "youtube.social_id",
                                        "youtube.age_range",
                                        "youtube.publisher_type",
                                        "youtube.min_compensation",
                                        "youtube.recent_medial_urls",
                                        "youtube.tags",
                                        "youtube.super_caption",
                                        "youtube.sourced_by",
                                        "youtube.last_imported_at",
                                        "invites.campaign_ids",
                                        "invites.campaign_names",
                                        "invites.pending",
                                        "invites.not_pending",
                                        "invites.approved",
                                        "invites.avg_invite_score",
                                        "posts.accepted",
                                        "posts.rejected",
                                        "posts.avg_quality",
                                        "posts.accepted_campaign_names",
                                        "posts.accepted_campaign_ids"
                                    `}
                                </dd>
                            </dl>
                        </div>
                    </ExpandablePanel>
                ) : null}
            </div>
        );

        return (
            <div className="advanced-filters">
                <div className="row">
                    <div className="col-md-8">
                        <div className="form-group">
                            <FormLabel
                                label="Select from Existing"
                                helpText="Select filter from an existing campaign"
                                inline
                            />
                            <CampaignFilterSelect
                                handleFilterSelect={this.handleFilterSelect}
                                campaignFuzzySearchUrl={campaignFuzzySearchUrl}
                                filtersApiUrl={filtersApiUrl}
                            />
                        </div>
                    </div>
                    <div className="pull-right advanced-options-section">
                        <a
                            className="toggle-advanced-options-button"
                            onClick={() =>
                                this.setState({
                                    showAdvancedOptions: !this.state.showAdvancedOptions
                                })
                            }
                        >
                            {showAdvancedOptions
                                ? 'Hide Advanced Options'
                                : 'Show Advanced Options'}
                        </a>
                        {this.renderGeneratedFilterSuggestions()}
                    </div>
                </div>
                <ReactTooltip />
                <div className="row">
                    <div className="col-md-4">
                        <div className="row">
                            <div className="panel-heading filter-heading">Source Details</div>
                            <div className="col-xs-12" style={{display: 'none'}}>
                                {sourcedBySelect}
                            </div>
                            <div className="col-xs-12">
                                <div className="form-group" style={{display: 'none'}}>
                                    <div className="row">
                                        <div className="col-lg-6">
                                            {lastImportedStartDatePicker}
                                        </div>
                                        <div className="col-lg-6">{lastImportedEndDatePicker}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-xs-12">
                                <div>{includeTagsMultiSelect}</div>
                                <div>{excludeTagsMultiSelect}</div>
                                <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                    {includeKeyValueTagsv2MultiSelectInput()}
                                </div>
                                <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                    {excludeKeyValueTagsv2MultiSelectInput()}
                                </div>
                                <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                    {tagsLink}
                                </div>
                                <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                    {includeContentAnalysisMultiSelectInput()}
                                </div>
                                <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                    {excludeContentAnalysisMultiSelectInput()}
                                </div>
                                <div>{includeInterestsMultiSelect}</div>
                                <div>{excludeInterestsMultiSelect}</div>
                            </div>
                            <div className="panel-heading filter-heading">Invite History</div>
                            <div className="col-xs-12">
                                <div
                                    style={{display: showAdvancedOptions ? '' : 'none'}}
                                    className="invertible"
                                >
                                    <div>{hasInviteForCampaignMultiSelect}</div>
                                </div>
                                <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                    {hasNoInviteForCampaignMultiSelect}
                                </div>
                                <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                    {hasInviteForSentFlightInput}
                                </div>
                                <div>{numberOfPendingInvitesRange}</div>
                                <div>{numberOfNonPendingInvitesRange}</div>
                                <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                    {numberOfInvitesSentRange}
                                </div>
                                <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                    {numberOfAdminApprovedCampaignsRange}
                                </div>
                                <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                    {inviteScoreRange}
                                </div>
                            </div>
                            <div className="panel-heading filter-heading">Toggles</div>
                            <div className="col-xs-12">{hasPhoneNumberCheckbox}</div>
                            <div className="col-xs-12">{hasCompleteProfileCheckbox}</div>
                            <div
                                className="col-xs-12"
                                style={{display: showAdvancedOptions ? 'inherit' : 'none'}}
                            >
                                {hasIncompleteProfileCheckbox}
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="row">
                            <div className="panel-heading filter-heading">
                                Social Platform Details
                            </div>
                            <div className="col-xs-12">
                                {metricsPlatformSelect}
                                {numberOfFollowersRange}
                                {referencePayoutAmountRange}
                                {engagementsPerPostRange}
                                {engagementRateRange}
                                {commentEngagementPercentageRange}
                            </div>
                            <div className="panel-heading filter-heading">Post History</div>
                            <div className="col-xs-12">
                                <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                    {totalAcceptedPostsRange}
                                </div>
                                <div>{totalRejectedPostsRange}</div>
                                <div>{averagePostQualityRange}</div>
                                <div>{averageVideoPostQualityRange}</div>
                                <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                    {totalPostsOnAccountRange}
                                </div>
                                <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                    {hasApprovedPostForCampaignMultiSelect}
                                </div>
                            </div>
                            <div className="panel-heading filter-heading">Results</div>
                            <div className="col-xs-12">
                                <div>{maxNewResultsInput}</div>
                                <div>{maxReactivatedResultsInput}</div>
                                <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                    {minScoreInput}
                                </div>
                            </div>
                            <div
                                className="secret-sauce-panel"
                                style={{display: showAdvancedOptions ? 'inherit' : 'none'}}
                            >
                                <div className="panel-heading filter-heading">Scoring</div>
                                <div className="col-xs-12 form-group">
                                    {scoringStrategySelect}
                                    {brandScoringSelect}
                                </div>
                            </div>
                            <div className="panel-heading filter-heading">
                                Instagram Video Metrics
                            </div>
                            <div className="col-xs-12 form-group">
                                {avgViewsPerVideoRange}
                                {recentVideoPercentRange}
                                {recentVideoViewsRange}
                                {trueVideoEngagementRateRange}
                                {videoViewRateRange}
                                {videoEngagementRateRange}
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="panel-heading filter-heading">Creator Details</div>
                        <div className="col-xs-12 creator-details-panel">
                            <div style={{display: showAdvancedOptions ? '' : 'none'}}>
                                {contentTypeSpecialtyMultiSelect}
                            </div>
                            <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                {primaryLanguageMultiSelect}
                            </div>
                            <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                {crawlCountryMultiSelect}
                            </div>
                            <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                {profileCountryMultiSelect}
                            </div>
                            <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                {publisherTypeMultiSelect}
                            </div>
                            <div>{ageRangeMultiSelect}</div>
                            <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                {isParentMultiSelect}
                            </div>
                            <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                {childrenAgeRangeMultiSelect}
                            </div>
                            <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                {cityInput}
                            </div>
                            <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                {regionInput}
                            </div>
                            <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                {hasLeadForPlatformFilter}
                            </div>
                            <div style={{display: showAdvancedOptions ? 'inherit' : 'none'}}>
                                {hasAuthForPlatformFilter}
                            </div>
                            <div
                                className="panel-heading filter-heading"
                                style={{display: showAdvancedOptions ? 'inherit' : 'none'}}
                            >
                                GPS Location
                                <HelpBubble
                                    text="Search within a circle defined by GPS coordinates and radius. This will only return registered users who have filled out their profile address"
                                    multiline
                                    inline
                                    place="right"
                                />
                            </div>
                            <div
                                className="col-xs-12 form-group"
                                style={{display: showAdvancedOptions ? 'inherit' : 'none'}}
                            >
                                {latitudeInput}
                                {longitudeInput}
                                {distanceMilesInput}
                            </div>
                            <div className="panel-heading filter-heading">
                                Creator Fuzzy Demographics
                            </div>
                            <div className="col-xs-12 fuzzy-demographics-options">
                                {demographicsCountriesMultiSelect}
                                {demographicsCitiesMultiSelect}
                                {demographicsIncludeInterestsMultiSelect}
                                {demographicsIncludeInterestsConfidenceInput}
                                {demographicsPublisherTypesMultiSelect}
                                {demographicsPublisherTypesConfidenceInput}
                            </div>
                        </div>
                        <div
                            className="col-xs-12"
                            style={{display: showAdvancedOptions ? 'inherit' : 'none'}}
                        >
                            {optionalFilters}
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12">{queryField}</div>
                    <div className="row">
                        <div className="col-xs-12">{actionButtons}</div>
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./publisher_leads/advanced_filters/index.js